import { useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
  breakpoints,
  fontSize,
  Modal,
  palette,
  spacing,
  Button,
} from '@playdapp/ui';

import Image from '@/components/Image';
import Icon from '@/components/Icon';

type Step = {
  buttonName: string;
  line1: string;
  line2: string;
  clickEvent: () => void;
};

type Props = {
  isOpen: boolean;
  image?: string;
  isSendClicked: boolean;
  handleOpen: (isOpen: boolean) => void;
  handleSend: () => void;
};

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ImageBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: ${spacing.m} 0;
  margin-bottom: ${spacing.m};
  background-color: ${palette.gray300};
  border-radius: 1rem;
`;

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const ImageBox = styled.figure`
  display: flex;
  align-items: center;

  margin-bottom: 0;

  ${breakpoints.down('md')} {
    max-height: 100px;
    max-width: 100px;
  }
`;

const PlusImage = styled(ImageBlock)`
  margin: 0;
  padding: 0;

  ${breakpoints.down('md')} {
    max-height: 30px;
    max-width: 30px;
  }
`;

const ModalTitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.s};
  margin-bottom: ${spacing.xxl};

  ${breakpoints.down('md')} {
    margin-bottom: ${spacing.s};
  }
`;

const ModalTitle = styled.h3`
  font-size: ${fontSize.h3};
  font-weight: 900;
  text-align: center;

  ${breakpoints.down('md')} {
    font-size: ${fontSize.h5};
  }
`;

const ModalSubTitle = styled.div`
  font-size: ${fontSize.p3};
  color: ${palette.gray900};
  text-align: center;
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.s};

  button {
    flex: 1;
    max-width: ${({ theme }) => theme.rem(205)};
    font-weight: 600;
  }

  ${breakpoints.down('md')} {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const BridgeSendModal = ({
  isOpen,
  isSendClicked,
  image,
  handleOpen,
  handleSend,
}: Props) => {
  const [step, setStep] = useState(0);

  const handleBridgeModal = (isModalOpen: boolean) => {
    handleOpen(isModalOpen);
    setStep(0);
  };

  const handleClickSendToPolygon = useCallback(() => {
    handleSend();
  }, [handleSend]);

  const handleNext = () => {
    setStep((prev) => prev + 1);
  };

  const steps: Step[] = useMemo(
    () => [
      {
        buttonName: 'Next',
        clickEvent: handleNext,
        line1: "Convert the selected NFT to a 'Polygon' network version",
        line2:
          'When you convert for the first time, you’ll receive an additional PlayDapp Town R grade NFT',
      },
      {
        buttonName: 'Send to Polygon',
        clickEvent: handleClickSendToPolygon,
        line1: 'Network converting may take up to 3 days',
        line2: "Follow your wallet's instructions to send your asset",
      },
    ],
    [handleClickSendToPolygon],
  );

  return (
    <Modal isOpen={isOpen} handleOpen={handleBridgeModal}>
      <ModalContent>
        <ImageBlock>
          <ImageContainer>
            <ImageBox>
              <Image
                src={image as string}
                alt="random-r"
                width="200px"
                height="200px"
              />
            </ImageBox>
            <PlusImage>
              <Icon name="random-plus" size={50} />
            </PlusImage>
            <ImageBox>
              <Image
                src="/assets/images/random-r.png"
                alt="random-r"
                width="200px"
                height="200px"
              />
            </ImageBox>
          </ImageContainer>
        </ImageBlock>
        <ModalTitleBlock>
          <ModalTitle>Send to Polygon ({step + 1}/2)</ModalTitle>
          <ModalSubTitle>
            <p>{steps[step].line1}</p>
            <p>{steps[step].line2}</p>
          </ModalSubTitle>
        </ModalTitleBlock>
        <ButtonGroup>
          <Button
            onClick={steps[step].clickEvent}
            isActive={isSendClicked}
            size="lg"
          >
            {steps[step].buttonName}
          </Button>
        </ButtonGroup>
      </ModalContent>
    </Modal>
  );
};

export default BridgeSendModal;
